import React, { useState, useContext, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Avatar,
  AvatarGroup,
  Chip,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider,
  Button,
  ButtonGroup,
  CircularProgress
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  EmojiEvents as TrophyIcon,
  SportsEsports as GameIcon,
  CheckCircle as ApprovedIcon,
  Cancel as PendingIcon,
  Close as CloseIcon,
  TrendingUp as RemontadaIcon,
  LocalFireDepartment as PalizaIcon,
  Extension as ComboIcon,
  Shield as ResilienciaIcon,
  Person as SobrevivienteIcon,
  Bolt as RapidaIcon,
  WatchLater as WatchLaterIcon,
  Close as RejectedIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import api from '../services/api';
import RaceChip from './RaceChip';
import { AuthContext } from '../context/AuthContext';
import useMatchActions from '../hooks/useMatchActions';

const getTierColor = (tier) => {
  switch (tier) {
    case 'Amistoso':
      return { bg: '#4caf50', text: '#fff' };
    case 'Amistoso c/ Arbitro':
      return { bg: '#2196f3', text: '#fff' };
    case 'Arena':
      return { bg: '#ff9800', text: '#fff' };
    case 'Torneo':
      return { bg: '#f44336', text: '#fff' };
    default:
      return { bg: 'action.hover', text: 'text.primary' };
  }
};

const getHighlightIcon = (highlight) => {
  switch (highlight) {
    case 'Remontada':
      return <RemontadaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Paliza':
      return <PalizaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Combo':
      return <ComboIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Resiliencia':
      return <ResilienciaIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Sobreviviente':
      return <SobrevivienteIcon sx={{ fontSize: '1.2rem' }} />;
    case 'Rapida':
      return <RapidaIcon sx={{ fontSize: '1.2rem' }} />;
    default:
      return null;
  }
};

const PendingMatchCard = ({ match, usersMap, onClick, showApprovalButtons = true, onUpdate }) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { user } = useContext(AuthContext);
  const { loading, handleApprove, handleReject, needsUserApproval } = useMatchActions(onUpdate);

  // Función para obtener los nombres de usuario a partir de los jugadores
  const getUsernamesFromPlayers = (players) => {
    console.log('Getting usernames for players:', players);
    if (!Array.isArray(players)) return [];
    return players.map(player => {
      const username = usersMap[player.user_id];
      console.log('Player ID:', player.user_id, 'Username:', username);
      return {
        id: player.user_id,
        name: username || player.user_id
      };
    });
  };

  // Función para obtener el estado de aprobación de un jugador
  const getPlayerApprovalStatus = (playerId, teamNumber) => {
    console.log('Getting approval status for player:', playerId, 'in team:', teamNumber);
    const team = teamNumber === 1 ? match.team1 : match.team2;
    console.log('Team data:', team);
    if (!team?.players) return null;
    
    const playerIdStr = String(playerId);
    const player = team.players.find(p => String(p.user_id) === playerIdStr);
    console.log('Found player:', player);
    
    // Si el jugador es el usuario actual y la partida fue rechazada, mostrar como rechazada
    if (player && String(player.user_id) === String(user?.id) && match.status === 'rejected') {
      return 'rejected';
    }
    
    return player?.approval_status;
  };

  // Función para obtener el equipo del usuario actual
  const getUserTeam = () => {
    console.log('Getting user team for user ID:', user.id);
    if (!match.team1?.players || !match.team2?.players) return null;
    
    const userId = String(user.id);
    console.log('String user ID:', userId);
    const team1HasUser = match.team1.players.some(p => String(p.user_id) === userId);
    const team2HasUser = match.team2.players.some(p => String(p.user_id) === userId);
    console.log('Team 1 has user:', team1HasUser);
    console.log('Team 2 has user:', team2HasUser);
    
    if (team1HasUser) return 1;
    if (team2HasUser) return 2;
    return null;
  };

  const getPlayerDecks = (teamNumber) => {
    const team = teamNumber === 1 ? match.team1 : match.team2;
    console.log(`Getting decks for team ${teamNumber}:`, team);
    
    if (!team?.decks || !Array.isArray(team.decks)) {
      console.log(`No decks found for team ${teamNumber}`);
      return [];
    }
    
    return team.decks.map(deck => ({
      name: deck.deck_name || 'Mazo sin nombre',
      race: deck.deck_raza || 'Desconocida'
    }));
  };

  const team1Players = getUsernamesFromPlayers(match.team1?.players || []);
  const team2Players = getUsernamesFromPlayers(match.team2?.players || []);
  const isTeam1Winner = match.winning_team === 'team1';

  console.log('DEBUG - Match data:', {
    match,
    team1Score: match.team1?.score,
    team2Score: match.team2?.score,
    team1Players,
    team2Players,
    isTeam1Winner,
    team1Decks: getPlayerDecks(1),
    team2Decks: getPlayerDecks(2)
  });

  const handleClick = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onClick) {
      await onClick(e, match);
    }
  }, [onClick, match]);

  const handleApproveClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (!match?.id) {
        console.error('No hay ID de partida para aprobar');
        return;
      }
      setIsProcessing(true);
      const success = await handleApprove(match.id);
      if (success && onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error('Error al aprobar la partida:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (!match?.id) {
        console.error('No hay ID de partida para rechazar');
        return;
      }
      setIsProcessing(true);
      const success = await handleReject(match.id);
      if (success && onUpdate) {
        await onUpdate();
      }
    } catch (error) {
      console.error('Error al rechazar la partida:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isProcessing) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 1.8,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'rgba(255, 152, 0, 0.5)',
          bgcolor: 'background.paper',
          transition: 'all 0.2s',
          position: 'relative',
          minHeight: '200px',
          opacity: 0.7,
          pointerEvents: 'none'
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            borderRadius: 2,
            zIndex: 1
          }}
        >
          <CircularProgress size={40} sx={{ color: 'rgba(255, 152, 0, 0.8)' }} />
        </Box>
        <Stack spacing={1.5} sx={{ filter: 'blur(1px)' }}>
          {/* Match Header */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'flex-start',
            gap: 0.8,
            flexWrap: 'wrap'
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
            }}>
              <ScheduleIcon sx={{ 
                color: 'text.secondary',
                fontSize: '1.26rem'
              }} />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'text.secondary',
                  fontSize: '0.84rem'
                }} 
                noWrap
              >
                {dayjs(match.match_date).format('DD/MM/YYYY HH:mm')}
              </Typography>
            </Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.8,
              ml: 'auto'
            }}>
              <Chip
                icon={<GameIcon sx={{ 
                  color: 'text.secondary',
                  fontSize: '1.26rem'
                }} />}
                label={match.match_mode}
                size="small"
                sx={{ 
                  height: '23px',
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  '& .MuiChip-label': { 
                    fontSize: '0.735rem',
                    px: 0.8
                  }
                }}
              />
              <Chip
                icon={<TrophyIcon sx={{ 
                  color: getTierColor(match.tier).text,
                  fontSize: '1.2rem'
                }} />}
                label={match.tier}
                size="small"
                sx={{ 
                  height: '22px',
                  bgcolor: getTierColor(match.tier).bg,
                  color: getTierColor(match.tier).text,
                  '& .MuiChip-label': { 
                    fontSize: '0.7rem',
                    px: 0.8
                  }
                }}
              />
            </Box>
          </Box>

          {/* Teams Section */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            py: 0.8
          }}>
            {/* Team 1 */}
            <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1,
                width: '100%',
                justifyContent: 'center'
              }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: isTeam1Winner ? '#1976d2' : 'text.secondary',
                    fontSize: { xs: '1.8rem', sm: '2.2rem' },
                    minWidth: '60px',
                    textAlign: 'center',
                    flexShrink: 0
                  }}
                >
                  {match.team1?.score || 0}
                </Typography>
                <Box sx={{ 
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minWidth: 0,
                  width: '100%'
                }}>
                  <Stack spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {team1Players.map((player, idx) => {
                        const approvalStatus = getPlayerApprovalStatus(player.id, 1);
                        const needsApproval = approvalStatus === 'pending';
                        const isRejected = approvalStatus === 'rejected';
                        
                        return (
                          <Badge
                            key={idx}
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={
                              <Box
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: '50%',
                                  bgcolor: 'background.paper',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: needsApproval ? '2px solid #ffd54f' : isRejected ? '2px solid #f44336' : 'none'
                                }}
                              >
                                {approvalStatus === 'approved' ? (
                                  <ApprovedIcon
                                    sx={{
                                      fontSize: 14,
                                      color: 'success.main',
                                    }}
                                  />
                                ) : isRejected ? (
                                  <RejectedIcon
                                    sx={{
                                      fontSize: 14,
                                      color: '#f44336',
                                    }}
                                  />
                                ) : (
                                  <WatchLaterIcon
                                    sx={{
                                      fontSize: 14,
                                      color: 'warning.main',
                                    }}
                                  />
                                )}
                              </Box>
                            }
                          >
                            <Avatar
                              sx={{
                                width: 26.8,
                                height: 26.8,
                                fontSize: '0.76rem',
                                bgcolor: isTeam1Winner ? '#1976d2' : 'grey.700',
                                border: '2px solid',
                                borderColor: needsApproval ? 'warning.main' : isRejected ? '#f44336' : (isTeam1Winner ? '#1976d2' : 'grey.500'),
                                color: needsApproval ? 'warning.main' : isRejected ? '#f44336' : 'white'
                              }}
                            >
                              {player.name[0]?.toUpperCase()}
                            </Avatar>
                          </Badge>
                        );
                      })}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={isTeam1Winner ? 'bold' : 'normal'}
                        sx={{ 
                          fontSize: { xs: '0.735rem', sm: '0.86rem' },
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: isTeam1Winner ? '#1976d2' : 'text.secondary'
                        }}
                      >
                        {team1Players.map((player, idx) => (
                          <React.Fragment key={idx}>
                            {player.name}{idx < team1Players.length - 1 ? ', ' : ''}
                          </React.Fragment>
                        ))}
                      </Typography>
                      {(() => {
                        const decks = getPlayerDecks(1);
                        console.log('Rendering decks for team 1:', decks);
                        return decks.map((deck, idx) => {
                          console.log('Rendering deck:', deck);
                          return (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <RaceChip race={deck.race} size="small" />
                            </Box>
                          );
                        });
                      })()}
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Stack>

            {/* VS */}
            <Typography 
              variant="body1" 
              sx={{ 
                mx: 1,
                fontSize: { xs: '0.86rem', sm: '0.99rem' },
                color: 'text.secondary',
                fontWeight: 'bold'
              }}
            >
              VS
            </Typography>

            {/* Team 2 */}
            <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: 1,
                width: '100%',
                flexDirection: 'row-reverse',
                justifyContent: 'center'
              }}>
                <Box sx={{ 
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minWidth: '60px'
                }}>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      fontWeight: 'bold',
                      color: !isTeam1Winner ? '#1976d2' : 'text.secondary',
                      fontSize: { xs: '1.8rem', sm: '2.2rem' },
                      textAlign: 'center'
                    }}
                  >
                    {match.team2?.score || 0}
                  </Typography>
                </Box>
                <Box sx={{ 
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%'
                }}>
                  <Stack spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', width: '100%' }}>
                      {team2Players.map((player, idx) => {
                        const approvalStatus = getPlayerApprovalStatus(player.id, 2);
                        const needsApproval = approvalStatus === 'pending';
                        const isRejected = approvalStatus === 'rejected';
                        
                        return (
                          <Badge
                            key={idx}
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            badgeContent={
                              <Box
                                sx={{
                                  width: 18,
                                  height: 18,
                                  borderRadius: '50%',
                                  bgcolor: 'background.paper',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  border: needsApproval ? '2px solid #ffd54f' : isRejected ? '2px solid #f44336' : 'none'
                                }}
                              >
                                {approvalStatus === 'approved' ? (
                                  <ApprovedIcon
                                    sx={{
                                      fontSize: 14,
                                      color: 'success.main',
                                    }}
                                  />
                                ) : isRejected ? (
                                  <RejectedIcon
                                    sx={{
                                      fontSize: 14,
                                      color: '#f44336',
                                    }}
                                  />
                                ) : (
                                  <WatchLaterIcon
                                    sx={{
                                      fontSize: 14,
                                      color: 'warning.main',
                                    }}
                                  />
                                )}
                              </Box>
                            }
                          >
                            <Avatar
                              sx={{
                                width: 26.8,
                                height: 26.8,
                                fontSize: '0.76rem',
                                bgcolor: !isTeam1Winner ? '#1976d2' : 'grey.700',
                                border: '2px solid',
                                borderColor: needsApproval ? 'warning.main' : isRejected ? '#f44336' : (!isTeam1Winner ? '#1976d2' : 'grey.500'),
                                color: needsApproval ? 'warning.main' : isRejected ? '#f44336' : 'white'
                              }}
                            >
                              {player.name[0]?.toUpperCase()}
                            </Avatar>
                          </Badge>
                        );
                      })}
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, width: '100%' }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={!isTeam1Winner ? 'bold' : 'normal'}
                        sx={{ 
                          fontSize: { xs: '0.735rem', sm: '0.86rem' },
                          textAlign: 'center',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          color: !isTeam1Winner ? '#1976d2' : 'text.secondary'
                        }}
                      >
                        {team2Players.map((player, idx) => (
                          <React.Fragment key={idx}>
                            {player.name}{idx < team2Players.length - 1 ? ', ' : ''}
                          </React.Fragment>
                        ))}
                      </Typography>
                      {(() => {
                        const decks = getPlayerDecks(2);
                        console.log('Rendering decks for team 2:', decks);
                        return decks.map((deck, idx) => {
                          console.log('Rendering deck:', deck);
                          return (
                            <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                              <RaceChip race={deck.race} size="small" />
                            </Box>
                          );
                        });
                      })()}
                    </Box>
                  </Stack>
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* Match Info - Highlights */}
          {(() => {
            try {
              if (!match.highlights || !Array.isArray(match.highlights) || match.highlights.length === 0) {
                console.log('No highlights found:', match.highlights);
                return null;
              }

              console.log('Processing highlights:', match.highlights);
              
              return (
                <Stack 
                  direction="row" 
                  spacing={0.4}
                  justifyContent="center"
                  flexWrap="wrap"
                  sx={{ gap: 0.4 }}
                >
                  {match.highlights.map((highlight, index) => {
                    const highlightText = highlight.type || highlight;
                    console.log('Rendering highlight:', highlightText);
                    
                    return (
                      <Chip
                        key={index}
                        icon={getHighlightIcon(highlightText)}
                        label={highlightText}
                        size="small"
                        sx={{ 
                          height: '22px',
                          bgcolor: '#0277bd',
                          color: 'white',
                          '& .MuiChip-label': { 
                            fontSize: '0.7rem',
                            px: 0.8
                          },
                          '& .MuiChip-icon': {
                            color: 'white',
                            fontSize: '1.2rem'
                          }
                        }}
                      />
                    );
                  })}
                </Stack>
              );
            } catch (error) {
              console.warn('Error processing highlights:', error);
              return null;
            }
          })()}

          {/* Approval Buttons */}
          {showApprovalButtons && needsUserApproval(match) && (
            <ButtonGroup 
              variant="contained" 
              sx={{ 
                width: '100%', 
                mt: 1,
                '& .MuiButton-root': {
                  flex: 1,
                  textTransform: 'none',
                  borderRadius: 1.5,
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <Button
                color="success"
                startIcon={<ApprovedIcon />}
                onClick={handleApproveClick}
                disabled={loading}
                sx={{ pointerEvents: 'auto' }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Aprobar'
                )}
              </Button>
              <Button
                color="error"
                startIcon={<RejectedIcon />}
                onClick={handleRejectClick}
                disabled={loading}
                sx={{ pointerEvents: 'auto' }}
              >
                {loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Rechazar'
                )}
              </Button>
            </ButtonGroup>
          )}
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      onClick={handleClick}
      sx={{
        p: 1.8,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'rgba(255, 152, 0, 0.5)',
        bgcolor: 'background.paper',
        transition: 'all 0.2s',
        cursor: 'pointer',
        '&:hover': {
          borderColor: 'rgba(255, 152, 0, 1)',
          boxShadow: '0 4px 8px rgba(255, 152, 0, 0.07)'
        }
      }}
    >
      <Stack spacing={1.5}>
        {/* Match Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'flex-start',
          gap: 0.8,
          flexWrap: 'wrap'
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.5,
          }}>
            <ScheduleIcon sx={{ 
              color: 'text.secondary',
              fontSize: '1.26rem'
            }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.84rem'
              }} 
              noWrap
            >
              {dayjs(match.match_date).format('DD/MM/YYYY HH:mm')}
            </Typography>
          </Box>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 0.8,
            ml: 'auto'
          }}>
            <Chip
              icon={<GameIcon sx={{ 
                color: 'text.secondary',
                fontSize: '1.26rem'
              }} />}
              label={match.match_mode}
              size="small"
              sx={{ 
                height: '23px',
                bgcolor: 'action.hover',
                color: 'text.primary',
                '& .MuiChip-label': { 
                  fontSize: '0.735rem',
                  px: 0.8
                }
              }}
            />
            <Chip
              icon={<TrophyIcon sx={{ 
                color: getTierColor(match.tier).text,
                fontSize: '1.2rem'
              }} />}
              label={match.tier}
              size="small"
              sx={{ 
                height: '22px',
                bgcolor: getTierColor(match.tier).bg,
                color: getTierColor(match.tier).text,
                '& .MuiChip-label': { 
                  fontSize: '0.7rem',
                  px: 0.8
                }
              }}
            />
          </Box>
        </Box>

        {/* Teams Section */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          py: 0.8
        }}>
          {/* Team 1 */}
          <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              width: '100%',
              justifyContent: 'center'
            }}>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 'bold',
                  color: isTeam1Winner ? '#1976d2' : 'text.secondary',
                  fontSize: { xs: '1.8rem', sm: '2.2rem' },
                  minWidth: '60px',
                  textAlign: 'center',
                  flexShrink: 0
                }}
              >
                {match.team1?.score || 0}
              </Typography>
              <Box sx={{ 
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: 0,
                width: '100%'
              }}>
                <Stack spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {team1Players.map((player, idx) => {
                      const approvalStatus = getPlayerApprovalStatus(player.id, 1);
                      const needsApproval = approvalStatus === 'pending';
                      const isRejected = approvalStatus === 'rejected';
                      
                      return (
                        <Badge
                          key={idx}
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                bgcolor: 'background.paper',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: needsApproval ? '2px solid #ffd54f' : isRejected ? '2px solid #f44336' : 'none'
                              }}
                            >
                              {approvalStatus === 'approved' ? (
                                <ApprovedIcon
                                  sx={{
                                    fontSize: 14,
                                    color: 'success.main',
                                  }}
                                />
                              ) : isRejected ? (
                                <RejectedIcon
                                  sx={{
                                    fontSize: 14,
                                    color: '#f44336',
                                  }}
                                />
                              ) : (
                                <WatchLaterIcon
                                  sx={{
                                    fontSize: 14,
                                    color: 'warning.main',
                                  }}
                                />
                              )}
                            </Box>
                          }
                        >
                          <Avatar
                            sx={{
                              width: 26.8,
                              height: 26.8,
                              fontSize: '0.76rem',
                              bgcolor: isTeam1Winner ? '#1976d2' : 'grey.700',
                              border: '2px solid',
                              borderColor: needsApproval ? 'warning.main' : isRejected ? '#f44336' : (isTeam1Winner ? '#1976d2' : 'grey.500'),
                              color: needsApproval ? 'warning.main' : isRejected ? '#f44336' : 'white'
                            }}
                          >
                            {player.name[0]?.toUpperCase()}
                          </Avatar>
                        </Badge>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2 }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={isTeam1Winner ? 'bold' : 'normal'}
                      sx={{ 
                        fontSize: { xs: '0.735rem', sm: '0.86rem' },
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: isTeam1Winner ? '#1976d2' : 'text.secondary'
                      }}
                    >
                      {team1Players.map((player, idx) => (
                        <React.Fragment key={idx}>
                          {player.name}{idx < team1Players.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                    </Typography>
                    {(() => {
                      const decks = getPlayerDecks(1);
                      console.log('Rendering decks for team 1:', decks);
                      return decks.map((deck, idx) => {
                        console.log('Rendering deck:', deck);
                        return (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <RaceChip race={deck.race} size="small" />
                          </Box>
                        );
                      });
                    })()}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>

          {/* VS */}
          <Typography 
            variant="body1" 
            sx={{ 
              mx: 1,
              fontSize: { xs: '0.86rem', sm: '0.99rem' },
              color: 'text.secondary',
              fontWeight: 'bold'
            }}
          >
            VS
          </Typography>

          {/* Team 2 */}
          <Stack alignItems="center" spacing={0.5} sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 1,
              width: '100%',
              flexDirection: 'row-reverse',
              justifyContent: 'center'
            }}>
              <Box sx={{ 
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: '60px'
              }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: !isTeam1Winner ? '#1976d2' : 'text.secondary',
                    fontSize: { xs: '1.8rem', sm: '2.2rem' },
                    textAlign: 'center'
                  }}
                >
                  {match.team2?.score || 0}
                </Typography>
              </Box>
              <Box sx={{ 
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%'
              }}>
                <Stack spacing={0.5} alignItems="center" sx={{ width: '100%' }}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', width: '100%' }}>
                    {team2Players.map((player, idx) => {
                      const approvalStatus = getPlayerApprovalStatus(player.id, 2);
                      const needsApproval = approvalStatus === 'pending';
                      const isRejected = approvalStatus === 'rejected';
                      
                      return (
                        <Badge
                          key={idx}
                          overlap="circular"
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                          badgeContent={
                            <Box
                              sx={{
                                width: 18,
                                height: 18,
                                borderRadius: '50%',
                                bgcolor: 'background.paper',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: needsApproval ? '2px solid #ffd54f' : isRejected ? '2px solid #f44336' : 'none'
                              }}
                            >
                              {approvalStatus === 'approved' ? (
                                <ApprovedIcon
                                  sx={{
                                    fontSize: 14,
                                    color: 'success.main',
                                  }}
                                />
                              ) : isRejected ? (
                                <RejectedIcon
                                  sx={{
                                    fontSize: 14,
                                    color: '#f44336',
                                  }}
                                />
                              ) : (
                                <WatchLaterIcon
                                  sx={{
                                    fontSize: 14,
                                    color: 'warning.main',
                                  }}
                                />
                              )}
                            </Box>
                          }
                        >
                          <Avatar
                            sx={{
                              width: 26.8,
                              height: 26.8,
                              fontSize: '0.76rem',
                              bgcolor: !isTeam1Winner ? '#1976d2' : 'grey.700',
                              border: '2px solid',
                              borderColor: needsApproval ? 'warning.main' : isRejected ? '#f44336' : (!isTeam1Winner ? '#1976d2' : 'grey.500'),
                              color: needsApproval ? 'warning.main' : isRejected ? '#f44336' : 'white'
                            }}
                          >
                            {player.name[0]?.toUpperCase()}
                          </Avatar>
                        </Badge>
                      );
                    })}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.2, width: '100%' }}>
                    <Typography 
                      variant="body2" 
                      fontWeight={!isTeam1Winner ? 'bold' : 'normal'}
                      sx={{ 
                        fontSize: { xs: '0.735rem', sm: '0.86rem' },
                        textAlign: 'center',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        color: !isTeam1Winner ? '#1976d2' : 'text.secondary'
                      }}
                    >
                      {team2Players.map((player, idx) => (
                        <React.Fragment key={idx}>
                          {player.name}{idx < team2Players.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      ))}
                    </Typography>
                    {(() => {
                      const decks = getPlayerDecks(2);
                      console.log('Rendering decks for team 2:', decks);
                      return decks.map((deck, idx) => {
                        console.log('Rendering deck:', deck);
                        return (
                          <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                            <RaceChip race={deck.race} size="small" />
                          </Box>
                        );
                      });
                    })()}
                  </Box>
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Box>

        {/* Match Info - Highlights */}
        {(() => {
          try {
            if (!match.highlights || !Array.isArray(match.highlights) || match.highlights.length === 0) {
              console.log('No highlights found:', match.highlights);
              return null;
            }

            console.log('Processing highlights:', match.highlights);
            
            return (
              <Stack 
                direction="row" 
                spacing={0.4}
                justifyContent="center"
                flexWrap="wrap"
                sx={{ gap: 0.4 }}
              >
                {match.highlights.map((highlight, index) => {
                  const highlightText = highlight.type || highlight;
                  console.log('Rendering highlight:', highlightText);
                  
                  return (
                    <Chip
                      key={index}
                      icon={getHighlightIcon(highlightText)}
                      label={highlightText}
                      size="small"
                      sx={{ 
                        height: '22px',
                        bgcolor: '#0277bd',
                        color: 'white',
                        '& .MuiChip-label': { 
                          fontSize: '0.7rem',
                          px: 0.8
                        },
                        '& .MuiChip-icon': {
                          color: 'white',
                          fontSize: '1.2rem'
                        }
                      }}
                    />
                  );
                })}
              </Stack>
            );
          } catch (error) {
            console.warn('Error processing highlights:', error);
            return null;
          }
        })()}

        {/* Approval Buttons */}
        {showApprovalButtons && needsUserApproval(match) && (
          <ButtonGroup 
            variant="contained" 
            sx={{ 
              width: '100%', 
              mt: 1,
              '& .MuiButton-root': {
                flex: 1,
                textTransform: 'none',
                borderRadius: 1.5,
              }
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Button
              color="success"
              startIcon={<ApprovedIcon />}
              onClick={handleApproveClick}
              disabled={loading}
              sx={{ pointerEvents: 'auto' }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Aprobar'
              )}
            </Button>
            <Button
              color="error"
              startIcon={<RejectedIcon />}
              onClick={handleRejectClick}
              disabled={loading}
              sx={{ pointerEvents: 'auto' }}
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Rechazar'
              )}
            </Button>
          </ButtonGroup>
        )}
      </Stack>
    </Paper>
  );
};

export default PendingMatchCard; 